<template>
    <b-modal :active="active" :width="460" @close="close">
        <div class="claim-celebration box" :class="isHero ? 'is-hero' : 'is-grid'">
            <div class="burst-layer" aria-hidden="true">
                <span
                    v-for="piece in burst"
                    :key="piece.id"
                    class="burst-piece"
                    :style="pieceStyle(piece)"></span>
            </div>

            <p class="claim-eyebrow">{{ eyebrow }}</p>

            <!-- One reward gets the whole stage: a single tile in a grid of
                 one reads as an afterthought, and this is the common case
                 (claiming a level as you reach it). -->
            <div v-if="isHero" class="claim-hero">
                <!-- `has-art` is narrower than `is-item`: an item without
                     resolved artwork falls through to a <rarity-gem>, and a
                     gem is a flat symbol that gains nothing from the extra
                     size. Only real shop artwork earns the bigger stage. -->
                <span
                    class="claim-art"
                    :class="{ 'is-item': hero.kind === 'item', 'has-art': hero.kind === 'item' && !!hero.icon }">
                    <img v-if="hero.icon" class="claim-art-icon" :src="hero.icon" alt="" />
                    <rarity-gem
                        v-else-if="hero.gemRarity"
                        class="claim-art-icon"
                        :rarity="hero.gemRarity" />
                    <span class="claim-shine" aria-hidden="true"></span>
                </span>
                <p v-if="hero.amount !== null" class="claim-amount">
                    +{{ counted.toLocaleString() }}
                </p>
                <!-- The counted number already says the quantity, so a
                     countable reward labels itself with just its unit —
                     "+280 / credits", not "+280 / 280 credits". -->
                <p class="claim-label">
                    {{ hero.amount !== null && hero.unit ? hero.unit : hero.label }}
                </p>
            </div>

            <!-- Claim-all. Every reward animates in on its own, staggered —
                 but as a grid rather than one-at-a-time: a 30-reward claim
                 stepped through individually is nearly a minute of clicking
                 through modals to reach a button. -->
            <template v-else>
                <ul class="claim-grid">
                    <li
                        v-for="(reward, i) in rewards"
                        :key="i"
                        class="claim-tile"
                        :class="`is-${reward.kind}`"
                        :style="tileStyle(i)">
                        <span class="claim-tile-art">
                            <img v-if="reward.icon" class="claim-tile-icon" :src="reward.icon" alt="" />
                            <rarity-gem
                                v-else-if="reward.gemRarity"
                                class="claim-tile-icon"
                                :rarity="reward.gemRarity" />
                        </span>
                        <span class="claim-tile-label">{{ reward.label }}</span>
                    </li>
                </ul>

                <!-- The grid says what arrived; this says how much, which is
                     the thing worth reading when there are thirty tiles. -->
                <p v-if="totals.length" class="claim-totals">
                    <span v-for="(total, i) in totals" :key="total.label" class="claim-total">
                        <span v-if="i > 0" class="claim-total-sep" aria-hidden="true">·</span>
                        <img v-if="total.icon" class="claim-total-icon" :src="total.icon" alt="" />
                        {{ total.text }}
                    </span>
                </p>
            </template>

            <b-button type="is-success" size="is-medium" expanded @click="close">Yippee!</b-button>
        </div>
    </b-modal>
</template>

<script>
import RarityGem from './RarityGem.vue'

/** Where the burst colours come from, per reward kind. */
const BURST_COLORS = {
    credits: ['#52D9E9', '#79eac1', '#32a6de'],
    gold: ['#DDC891', '#EBD26C', '#f3ebd5'],
    item: ['#E952D1', '#9370DB', '#52D9E9'],
}

/**
 * The "you got something" moment for level rewards.
 *
 * Two shapes, because one claim and thirty claims are different problems:
 *
 *  - **Hero** (a single reward) — the reward fills the stage, the number
 *    counts up, and the burst fires behind it.
 *  - **Grid** (claim-all) — every reward still animates in on its own, but
 *    staggered into a grid rather than sequenced through one at a time.
 *    Thirty rewards at ~1.5s each is a minute of watching modals before you
 *    can dismiss anything, which stops being a reward and starts being a
 *    toll.
 *
 * Rewards arrive already described (`{ kind, icon, gemRarity, amount, label }`)
 * rather than raw: the mapping from a reward to its icon, gem and wording
 * already exists on the profile page, and a second copy here is a second
 * thing to keep in step.
 */
export default {
    name: 'ClaimCelebration',
    components: { RarityGem },
    props: {
        active: { type: Boolean, default: false },
        rewards: { type: Array, default: () => [] },
    },
    data() {
        return {
            burst: [],
            counted: 0,
        }
    },
    computed: {
        isHero() { return this.rewards.length === 1 },
        hero() {
            return this.rewards[0] || { icon: null, gemRarity: null, amount: null, label: '' }
        },
        eyebrow() {
            if (this.isHero) return 'You claimed'
            return `You claimed ${this.rewards.length} rewards`
        },
        prefersReducedMotion() {
            return window.matchMedia('(prefers-reduced-motion: reduce)').matches
        },
        /** Credits, gold and item counts rolled up — the claim-all headline. */
        totals() {
            let credits = 0
            let gold = 0
            let items = 0
            let creditsIcon = null
            let goldIcon = null

            for (const reward of this.rewards) {
                if (reward.kind === 'credits') {
                    credits += reward.amount || 0
                    creditsIcon = reward.icon
                } else if (reward.kind === 'gold') {
                    gold += reward.amount || 0
                    goldIcon = reward.icon
                } else {
                    items++
                }
            }

            const out = []
            if (credits) out.push({ label: 'credits', icon: creditsIcon, text: credits.toLocaleString() })
            if (gold) out.push({ label: 'gold', icon: goldIcon, text: String(gold) })
            if (items) out.push({ label: 'items', icon: null, text: `${items} ${items === 1 ? 'item' : 'items'}` })
            return out
        },
    },
    watch: {
        active(open) {
            if (open) this.start()
            else this.teardown()
        },
    },
    beforeDestroy() {
        this.teardown()
    },
    methods: {
        start() {
            this.fireBurst()
            this.countUp()
        },

        /**
         * Counts the hero number up from zero.
         *
         * rAF is paused outright in a background tab, so a claim made just
         * before switching away would otherwise sit on 0 forever — the
         * timeout is what guarantees the final value lands either way. Same
         * bargain the purchase modal strikes for its balance counter.
         */
        countUp() {
            const target = this.isHero && this.hero.amount !== null ? this.hero.amount : 0
            this.counted = target
            if (!target || this.prefersReducedMotion) return

            this.counted = 0
            const duration = 650
            const started = performance.now()

            const tick = now => {
                const progress = Math.min((now - started) / duration, 1)
                // Ease-out: the number sprints then settles, rather than
                // crawling at a constant rate.
                this.counted = Math.round(target * (1 - Math.pow(1 - progress, 3)))
                if (progress < 1) this._raf = requestAnimationFrame(tick)
            }

            this._raf = requestAnimationFrame(tick)
            this._fallback = setTimeout(() => { this.counted = target }, duration + 400)
        },

        /** Generated rather than fixed, so no two claims burst identically. */
        fireBurst() {
            if (this.prefersReducedMotion) return

            const kinds = this.rewards.map(reward => reward.kind)
            const palette = kinds.reduce((colors, kind) => colors.concat(BURST_COLORS[kind] || BURST_COLORS.item), [])
            const pieces = []

            for (let i = 0; i < 40; i++) {
                pieces.push({
                    id: i,
                    left: Math.random() * 100,
                    color: palette[i % palette.length],
                    size: 5 + Math.random() * 6,
                    delay: Math.random() * 0.35,
                    duration: 1.6 + Math.random() * 1.2,
                    drift: (Math.random() - 0.5) * 140,
                    spin: `${(Math.random() - 0.5) * 720}deg`,
                })
            }
            this.burst = pieces
        },

        pieceStyle(piece) {
            return {
                left: `${piece.left}%`,
                width: `${piece.size}px`,
                height: `${piece.size}px`,
                backgroundColor: piece.color,
                animationDelay: `${piece.delay}s`,
                animationDuration: `${piece.duration}s`,
                '--drift': `${piece.drift}px`,
                '--spin': piece.spin,
            }
        },

        /**
         * Staggers the grid. Capped, so a thirty-reward claim doesn't take
         * three seconds to finish arriving — past a dozen the stagger has
         * already read as "one at a time" and the rest is just waiting.
         */
        tileStyle(index) {
            if (this.prefersReducedMotion) return null
            return { animationDelay: `${Math.min(index, 12) * 0.07}s` }
        },

        teardown() {
            if (this._raf) cancelAnimationFrame(this._raf)
            if (this._fallback) clearTimeout(this._fallback)
            this._raf = null
            this._fallback = null
            this.burst = []
        },

        close() {
            this.teardown()
            this.$emit('close')
        },
    },
}
</script>

<style lang="scss" scoped>
.claim-celebration {
    position: relative;
    overflow: hidden;
    text-align: center;
    padding: 1.15rem 1.1rem 1.1rem;
    border-radius: 1rem;
}

.claim-eyebrow {
    font-size: 0.8rem;
    font-weight: bold;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    opacity: 0.6;
    margin-bottom: 0.65rem;
}

// ── Hero (a single reward) ────────────────────────────────────────────────

.claim-art {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 6rem;
    height: 6rem;
    margin-bottom: 0.5rem;
    // Clips the shine sweep to the artwork rather than letting it run across
    // the whole modal.
    overflow: hidden;
    border-radius: 1rem;
    animation: claim-pop 0.45s cubic-bezier(0.2, 1.5, 0.4, 1) both;
}

// Shop artwork is a square photo that can be any colour, including white —
// the pale card backs and light themes have no edge of their own and simply
// vanish against the modal. A light slab behind them guarantees a boundary
// whatever the art turns out to be. Credits and gold don't need it: they're
// fixed, high-contrast shapes we control.
.claim-art.is-item,
.claim-tile.is-item .claim-tile-art {
    background-color: rgba(0, 0, 0, 0.07);
}

.claim-art.is-item {
    // Inset a little so the slab reads as a mount rather than a border.
    padding: 0.5rem;
}

// Shop artwork is the reward — the thing being handed over is a picture, and
// at 4.5rem a painted card back or profile background reads as a thumbnail of
// something rather than the something. Credits and gold keep the smaller
// stage: they're fixed two-colour marks that say everything they have to say
// small, and the number beneath them is the actual payload.
.claim-art.has-art {
    width: 9rem;
    height: 9rem;

    .claim-art-icon {
        width: 8rem;
        height: 8rem;
    }
}

.claim-art-icon {
    width: 4.5rem;
    height: 4.5rem;
    object-fit: contain;
}

// A single pass of light across the reward, once, on arrival.
.claim-shine {
    position: absolute;
    top: 0;
    left: -60%;
    width: 55%;
    height: 100%;
    background: linear-gradient(
        100deg,
        rgba(255, 255, 255, 0) 0%,
        rgba(255, 255, 255, 0.55) 50%,
        rgba(255, 255, 255, 0) 100%
    );
    transform: skewX(-18deg);
    animation: claim-shine 0.9s ease-out 0.3s both;
}

.claim-amount {
    font-size: 2.25rem;
    font-weight: bold;
    line-height: 1;
    margin: 0.25rem 0 0.15rem;
}

.claim-label {
    font-size: 1rem;
    opacity: 0.75;
    margin-bottom: 0.9rem;
}

// ── Grid (claim all) ──────────────────────────────────────────────────────

.claim-grid {
    display: grid;
    // Small enough that thirty tiles stay on one screen, big enough to read
    // the icon and its wording.
    grid-template-columns: repeat(auto-fill, minmax(6.25rem, 1fr));
    gap: 0.5rem;
    margin-bottom: 1rem;
    // Thirty rewards is taller than a modal; scroll rather than overflow the
    // viewport and push the dismiss button off-screen.
    max-height: 17rem;
    overflow-y: auto;
    padding: 0.15rem;
    margin-bottom: 0.75rem;
}

.claim-tile {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.3rem;
    padding: 0.6rem 0.35rem;
    border-radius: 0.6rem;
    background-color: rgba(0, 0, 0, 0.04);
    animation: claim-pop 0.4s cubic-bezier(0.2, 1.5, 0.4, 1) both;

    &.is-gold { background-color: rgba(221, 200, 145, 0.16); }
    &.is-credits { background-color: rgba(82, 217, 233, 0.14); }
    &.is-item { background-color: rgba(233, 82, 209, 0.12); }
}

.claim-tile-art {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 0.4rem;
}

.claim-tile.is-item .claim-tile-art {
    padding: 0.2rem;
}

.claim-tile-icon {
    width: 2rem;
    height: 2rem;
    object-fit: contain;
    display: block;
}

.claim-tile-label {
    font-size: 0.7rem;
    font-weight: bold;
    line-height: 1.15;
}

.claim-totals {
    font-size: 0.95rem;
    font-weight: bold;
    margin-bottom: 0.8rem;
}

.claim-total-icon {
    width: 1.1rem;
    height: 1.1rem;
    vertical-align: -0.2em;
    margin-right: 0.15rem;
}

.claim-total-sep {
    margin: 0 0.4rem;
    opacity: 0.4;
}

// ── Burst ─────────────────────────────────────────────────────────────────

.burst-layer {
    position: absolute;
    inset: 0;
    pointer-events: none;
    overflow: hidden;
    // Behind the content: the number and the label have to stay readable
    // through it.
    z-index: 0;
}

.burst-piece {
    position: absolute;
    top: -12px;
    border-radius: 2px;
    animation-name: claim-confetti;
    animation-timing-function: ease-in;
    animation-fill-mode: forwards;
}

.claim-celebration > *:not(.burst-layer) {
    position: relative;
    z-index: 1;
}

@keyframes claim-pop {
    from { transform: scale(0.6); opacity: 0; }
    to { transform: scale(1); opacity: 1; }
}

@keyframes claim-shine {
    from { left: -60%; }
    to { left: 115%; }
}

// --drift and --spin come from each piece's inline style, so one set of
// keyframes covers the whole burst however many pieces it has.
@keyframes claim-confetti {
    0% { opacity: 1; transform: translate(0, 0) rotate(0deg); }
    100% { opacity: 0; transform: translate(var(--drift), 300px) rotate(var(--spin)); }
}

// The reward still reads through colour, size and wording with nothing
// moving — so everything here is decoration, and all of it goes.
@media (prefers-reduced-motion: reduce) {
    .claim-art,
    .claim-tile {
        animation: none;
    }
    .claim-shine {
        display: none;
    }
}
</style>
